import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { environment } from '@environments/environments';
import { AdminUsersService } from '@office/users/users.service';
import { HttpQueryParams } from '@shared/models/domain/custom-views.model';
import { UserBankAccount } from '@shared/models/domain/domain.model';
import { PageRequest } from '@shared/models/request/page-request.model';
import { UserBankAccountRequest } from '@shared/models/request/user-bank-account-request.model';
import { PageResponse } from '@shared/models/response/page-response.model';
import { CoreApiService } from '@shared/services/common/core-api.service';
import { CustomerProjectService } from '@shared/services/customer/customer-warehouse.service';
import { BehaviorSubject, Observable, filter, map, of, switchMap, take } from 'rxjs';

export interface IBankAccountService {
    getClientToken(reference?: string): Observable<string>;
    getClientTokenSecure(queryParams: HttpQueryParams): Observable<string>;
    customCreate(item: UserBankAccountRequest): Observable<UserBankAccount>;
    customUpdate(id: number | string, item: UserBankAccountRequest): Observable<UserBankAccount>;
}

@Injectable({ providedIn: 'root' })
export class BankAccountService extends CoreApiService<UserBankAccount> implements IBankAccountService {
    private httpClient: HttpClient;
    private authService = inject(AuthService);

    private _userBankAccounts = new BehaviorSubject<PageResponse<UserBankAccount> | null>(null);
    private adminUsersService: AdminUsersService;
    private customerProjectService: CustomerProjectService;

    constructor() {
        const httpClient = inject(HttpClient);

        super('bank-account');
    
        this.httpClient = httpClient;
    }

    get userBankAccounts$(): Observable<PageResponse<UserBankAccount>> {
        return this._userBankAccounts.asObservable();
    }

    // Setter for AdminUsersService to avoid circular dependency
    setAdminUsersService(service: AdminUsersService): void {
        this.adminUsersService = service;
    }

    // Setter for CustomerProjectService to avoid circular dependency
    setCustomerProjectService(service: CustomerProjectService): void {
        this.customerProjectService = service;
    }

    getClientToken(reference?: string): Observable<string> {
        const options = reference ? { params: { reference } } : undefined;
        return this.httpClient.get<PageResponse<string>>(`${environment.apiUrl}api/${this.endpoint}/client-token`, options)
            .pipe(map(response => response.item));
    }

    getClientTokenSecure(queryParams: HttpQueryParams): Observable<string> {
        return this.httpClient.get<PageResponse<string>>(`${environment.apiUrl}api/${this.endpoint}/client-token/secure`, { params: queryParams })
            .pipe(map(response => response.item));
    }

    customCreate(item: UserBankAccountRequest): Observable<UserBankAccount> {
        return this.httpClient.post<PageResponse<UserBankAccount>>(`${environment.apiUrl}api/${this.endpoint}/custom`, item)
            .pipe(map(response => response.item));
    }

    customUpdate(id: number | string, item: UserBankAccountRequest): Observable<UserBankAccount> {
        return this.httpClient.put<PageResponse<UserBankAccount>>(`${environment.apiUrl}api/${this.endpoint}/${id}/custom`, item)
            .pipe(map(response => response.item));
    }

    getUserBankAccounts(userId: string, request: PageRequest): Observable<PageResponse<UserBankAccount>> {
        request = this.getRequest(request);
        request.find['UserId'] = userId ?? this.authService?.userProfile?.userProfile?.userId;

        return this.httpClient.get<PageResponse<UserBankAccount>>(`${environment.apiUrl}api/${this.endpoint}`, { params: request.toParams() }).pipe(
            switchMap((data) => {
                const response = data;
                return this.getProjectsObservable().pipe(
                    map((projects) => {
                        this.populateSideMark(response.items, projects);
                        this._userBankAccounts.next(response);
                        return response;
                    })
                );
            })
        );
    }

    private getProjectsObservable(): Observable<{ bankAccountId?: string | null; name?: string }[]> {
        // Office context: use subscription projects
        if (this.adminUsersService?.userSubscription$) {
            return this.adminUsersService.userSubscription$.pipe(
                filter(subscription => !!subscription),
                take(1),
                map((subscription) => subscription?.projects?.map(p => p?.project) || [])
            );
        }

        // Customer context: use customer projects observer
        if (this.customerProjectService?.observer$) {
            return this.customerProjectService.observer$.pipe(
                filter(response => !!response && !!response.items),
                take(1),
                map((response) => response?.items || [])
            );
        }

        // No context available: return empty array
        return of<{ bankAccountId?: string | null; name?: string }[]>([]);
    }

    private populateSideMark(bankAccounts: UserBankAccount[], projects: { bankAccountId?: string | null; name?: string }[]): void {
        bankAccounts?.forEach(bankAccount => {
            const matchingProjects = projects.filter(p => p?.bankAccountId === bankAccount?.id);
            bankAccount['sideMark'] = matchingProjects.map(p => p?.name).join(', ');
        });
    }
}